import { useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { languagesSpoken } from "../../../../constants/CreateProfileLists";
import PhotoDropzone from "../../components/PhotoDropzone";
import { useFlowActions, useFlowState } from "../../state/FlowProvider";
import { saveFlowState } from "../../state/persistence";
import {
  nextStep,
  ONBOARDING_BASE_PATH,
  profileIsComplete,
  profileIsValid,
} from "../registry";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";
import { MAX_ADDITIONAL_PHOTOS } from "../../../../helpers/imageUpload";
import { splitFullName } from "../../../../helpers/profileName";
import FormSubmitButton from "../../components/FormSubmitButton";

const EMPTY_ADDRESS = {
  city: "",
  province: "",
  postalCode: "",
  country: "",
};

// Frame widths from the Figma export's PortraitUpload: the main photo's maximum,
// and the width its empty additional frames render at.
const MAIN_PHOTO_WIDTH = 200;
const ADDITIONAL_PHOTO_WIDTH = 150;
const MAX_PAST_ROLES = 49;

const ProfileStep = ({ step }) => {
  const state = useFlowState();
  const { mergeProfile } = useFlowActions();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => ({
    firstName: "",
    lastName: "",
    role: "",
    biography: "",
    languages: [],
    additionalPhotos: [],
    ...state.profile,
    ...(state.profile?.firstName || state.profile?.lastName
      ? {}
      : splitFullName(state.profile?.name)),
    address: { ...EMPTY_ADDRESS, ...state.profile.address },
    currentRole: {
      title: state.profile.currentRole?.title ?? state.profile.role ?? "",
      organization: state.profile.currentRole?.organization ?? "",
    },
    pastRoles: state.profile.pastRoles ?? [],
  }));
  const [touched, setTouched] = useState({});
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(() => new Set());
  const photoRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const cityRef = useRef(null);
  const provinceRef = useRef(null);

  const update = (field, value) =>
    setProfile((current) => ({ ...current, [field]: value }));
  const updateAddress = (field, value) =>
    setProfile((current) => ({
      ...current,
      address: { ...current.address, [field]: value },
    }));
  const touch = (field) =>
    setTouched((current) => ({ ...current, [field]: true }));

  // A required part is flagged once it has been left, or once another part of
  // the location has text, so an untouched form stays quiet.
  const hasAddress = Object.values(profile.address).some((value) =>
    value.trim(),
  );
  const missing = (field) =>
    (touched[field] || hasAddress || showRequiredErrors) &&
    !profile.address[field].trim();
  const profileComplete = profileIsComplete({ profile });
  const currentTitle = profile.currentRole.title.trim();
  const currentOrganization = profile.currentRole.organization.trim();
  const currentRoleInvalid =
    Boolean(currentOrganization) !== Boolean(currentTitle);
  const pastRolesInvalid = profile.pastRoles.some(
    ({ title, organization }) => !title.trim() || !organization.trim(),
  );
  const uploadsPending = uploadingPhotos.size > 0;
  const canContinue = profileIsValid({ profile }) && !uploadsPending;
  const updateCurrentRole = (field, value) =>
    setProfile((current) => ({
      ...current,
      currentRole: { ...current.currentRole, [field]: value },
    }));
  const updatePastRole = (index, field, value) =>
    setProfile((current) => ({
      ...current,
      pastRoles: current.pastRoles.map((role, i) =>
        i === index ? { ...role, [field]: value } : role,
      ),
    }));
  const setPhotoUploading = (slot, uploading) =>
    setUploadingPhotos((current) => {
      const next = new Set(current);
      if (uploading) next.add(slot);
      else next.delete(slot);
      return next;
    });

  const handleReauthenticate = () => {
    const draft = { ...profile, role: "" };
    delete draft.name;
    // Save even an incomplete profile. Navigation remounts the provider, so
    // write through instead of relying on a dispatch's persistence effect.
    saveFlowState({ ...state, profile: draft });
    navigate("/login", { replace: true, state: { sessionExpired: true } });
  };

  const handleContinue = () => {
    if (!canContinue) {
      setShowRequiredErrors(true);
      const firstMissing = [
        [!profile.mainPhoto?.url, photoRef],
        [!profile.firstName.trim(), firstNameRef],
        [!profile.lastName.trim(), lastNameRef],
        [!profile.address.city.trim(), cityRef],
        [!profile.address.province.trim(), provinceRef],
      ].find(([isMissing]) => isMissing);
      firstMissing?.[1].current?.focus();
      return;
    }
    // Every key, trimmed: the server requires all four once a location is sent.
    const address = Object.fromEntries(
      Object.entries(profile.address).map(([key, value]) => [
        key,
        value.trim(),
      ]),
    );
    const currentProfile = { ...profile };
    delete currentProfile.name;
    const saved = {
      ...currentProfile,
      // mergeProfile preserves absent keys, so explicitly clear a resumed
      // title-only role when replacing it with the structured current role.
      role: "",
      firstName: profile.firstName.trim(),
      lastName: profile.lastName.trim(),
      currentRole: currentTitle
        ? { title: currentTitle, organization: currentOrganization }
        : null,
      pastRoles: profile.pastRoles.map(({ title, organization }) => ({
        title: title.trim(),
        organization: organization.trim(),
      })),
      address,
    };
    mergeProfile(saved);
    const next = nextStep(step.id, { ...state, profile: saved });
    if (next) navigate(`${ONBOARDING_BASE_PATH}/${next.path}`);
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (event.nativeEvent.submitter?.dataset.profileSubmit !== "true") {
          return;
        }
        handleContinue();
      }}
    >
      <Typography variant="h1" sx={{ mb: 1.5 }}>
        {t("onboarding.profile.heading")}
      </Typography>
      <Typography sx={{ color: onboardingTokens.mutedForeground, mb: 4 }}>
        {t("onboarding.profile.body")}
      </Typography>

      {/* Required fields first: photo, name, location. */}
      <Stack gap={3}>
        <Typography
          component="h2"
          sx={{ fontSize: "1.125rem", fontWeight: 600 }}
        >
          {t("onboarding.profile.requiredHeading")}
        </Typography>
        <PhotoDropzone
          label={t("onboarding.profile.mainPhoto")}
          hint={t("onboarding.profile.mainPhotoHint")}
          required
          focusRef={photoRef}
          validationError={
            showRequiredErrors && !profile.mainPhoto?.url
              ? t("onboarding.profile.mainPhotoRequired")
              : ""
          }
          photo={profile.mainPhoto}
          onReauthenticate={handleReauthenticate}
          onUploadingChange={(uploading) =>
            setPhotoUploading("main", uploading)
          }
          onChange={(photo) => update("mainPhoto", photo)}
          onRemove={() => update("mainPhoto", null)}
          width={MAIN_PHOTO_WIDTH}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 2,
          }}
        >
          <TextField
            required
            label={t("onboarding.profile.firstName")}
            value={profile.firstName}
            onChange={(event) => update("firstName", event.target.value)}
            inputRef={firstNameRef}
            error={showRequiredErrors && !profile.firstName.trim()}
            helperText={
              showRequiredErrors && !profile.firstName.trim()
                ? t("onboarding.profile.firstNameRequired")
                : ""
            }
            fullWidth
          />
          <TextField
            required
            label={t("onboarding.profile.lastName")}
            value={profile.lastName}
            onChange={(event) => update("lastName", event.target.value)}
            inputRef={lastNameRef}
            error={showRequiredErrors && !profile.lastName.trim()}
            helperText={
              showRequiredErrors && !profile.lastName.trim()
                ? t("onboarding.profile.lastNameRequired")
                : ""
            }
            fullWidth
          />
        </Box>

        <Box component="fieldset" sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}>
          <Typography
            component="legend"
            sx={{ fontWeight: 600, mb: 1.5, p: 0 }}
          >
            {t("onboarding.profile.address")}
            {" *"}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            <TextField
              required
              label={t("onboarding.profile.city")}
              value={profile.address.city}
              onChange={(event) => updateAddress("city", event.target.value)}
              inputRef={cityRef}
              onBlur={() => touch("city")}
              error={missing("city")}
              helperText={
                missing("city") ? t("onboarding.profile.cityRequired") : ""
              }
              fullWidth
            />
            <TextField
              required
              label={t("onboarding.profile.province")}
              value={profile.address.province}
              onChange={(event) =>
                updateAddress("province", event.target.value)
              }
              inputRef={provinceRef}
              onBlur={() => touch("province")}
              error={missing("province")}
              helperText={
                missing("province")
                  ? t("onboarding.profile.provinceRequired")
                  : ""
              }
              fullWidth
            />
          </Box>
        </Box>

        <Box
          component="section"
          aria-labelledby="optional-profile-heading"
          sx={{
            borderTop: `1px solid ${onboardingTokens.track}`,
            pt: 3,
          }}
        >
          <Typography
            id="optional-profile-heading"
            component="h2"
            sx={{ fontSize: "1.125rem", fontWeight: 600, mb: 0.75 }}
          >
            {t("onboarding.profile.optionalHeading")}
          </Typography>
          <Typography sx={{ color: onboardingTokens.mutedForeground, mb: 2 }}>
            {t("onboarding.profile.optionalBody")}
          </Typography>
          <FormSubmitButton
            type="button"
            variant="outlined"
            fullWidth
            disabledReason={
              uploadsPending ? t("onboarding.profile.uploadPending") : ""
            }
            onClick={handleContinue}
          >
            {t("onboarding.profile.skipOptional")}
          </FormSubmitButton>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 2,
          }}
        >
          <TextField
            label={t("onboarding.profile.postalCode")}
            value={profile.address.postalCode}
            onChange={(event) =>
              updateAddress("postalCode", event.target.value)
            }
            fullWidth
          />
          <TextField
            label={t("onboarding.profile.country")}
            value={profile.address.country}
            onChange={(event) => updateAddress("country", event.target.value)}
            fullWidth
          />
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600 }}>
            {t("onboarding.profile.additionalPhotos")}
          </Typography>
          <Typography
            sx={{
              mb: 1.5,
              fontSize: "0.8125rem",
              color: onboardingTokens.mutedForeground,
            }}
          >
            {t("onboarding.profile.additionalPhotosHint")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(3, minmax(0, ${ADDITIONAL_PHOTO_WIDTH}px))`,
              gap: 1.5,
            }}
          >
            {Array.from({ length: MAX_ADDITIONAL_PHOTOS }, (_, index) => (
              <PhotoDropzone
                key={index}
                label={`${t("onboarding.profile.additionalPhotos")} ${index + 1}`}
                hideLabel
                // Three to a row, so these render well under their 150px
                // maximum on a phone and the frame has no room for label text.
                compact
                width={ADDITIONAL_PHOTO_WIDTH}
                photo={profile.additionalPhotos[index]}
                onReauthenticate={handleReauthenticate}
                onUploadingChange={(uploading) =>
                  setPhotoUploading(`additional-${index}`, uploading)
                }
                onChange={(photo) =>
                  setProfile((current) => {
                    const additionalPhotos = [...current.additionalPhotos];
                    additionalPhotos[index] = photo;
                    return { ...current, additionalPhotos };
                  })
                }
                onRemove={() =>
                  setProfile((current) => ({
                    ...current,
                    additionalPhotos: current.additionalPhotos.filter(
                      (_, photoIndex) => photoIndex !== index,
                    ),
                  }))
                }
              />
            ))}
          </Box>
        </Box>

        <Box
          component="section"
          aria-label={t("onboarding.profile.employmentHeading")}
        >
          <Typography
            component="h2"
            sx={{ fontSize: "1.125rem", fontWeight: 600, mb: 2 }}
          >
            {t("onboarding.profile.employmentHeading")}
          </Typography>
          <Stack gap={2}>
            <Box
              component="fieldset"
              sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
            >
              <Typography component="legend" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t("onboarding.profile.currentJob")}
              </Typography>
              <Typography
                sx={{ color: onboardingTokens.mutedForeground, mb: 1.5 }}
              >
                {t("onboarding.profile.currentJobHint")}
              </Typography>
              <Stack gap={2}>
                <TextField
                  label={t("onboarding.profile.role")}
                  value={profile.currentRole.title}
                  onChange={(event) =>
                    updateCurrentRole("title", event.target.value)
                  }
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                  placeholder="e.g. Commercial Pilot"
                  error={
                    showRequiredErrors && currentRoleInvalid && !currentTitle
                  }
                  helperText={
                    showRequiredErrors && currentRoleInvalid && !currentTitle
                      ? t("onboarding.profile.currentRoleRequired")
                      : ""
                  }
                  fullWidth
                />
                <TextField
                  label={t("onboarding.profile.company")}
                  value={profile.currentRole.organization}
                  onChange={(event) =>
                    updateCurrentRole("organization", event.target.value)
                  }
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                  error={
                    showRequiredErrors &&
                    currentRoleInvalid &&
                    !currentOrganization
                  }
                  fullWidth
                />
              </Stack>
            </Box>
            {profile.pastRoles.map((role, index) => (
              <Box
                key={index}
                component="fieldset"
                sx={{ border: 0, p: 0, m: 0 }}
              >
                <Typography component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("onboarding.profile.pastRole")} {index + 1}
                </Typography>
                <Stack gap={2}>
                  <TextField
                    label={t("onboarding.profile.pastRoleTitle")}
                    value={role.title}
                    onChange={(event) =>
                      updatePastRole(index, "title", event.target.value)
                    }
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                    error={showRequiredErrors && !role.title.trim()}
                    fullWidth
                  />
                  <TextField
                    label={t("onboarding.profile.pastCompany")}
                    value={role.organization}
                    onChange={(event) =>
                      updatePastRole(index, "organization", event.target.value)
                    }
                    slotProps={{ htmlInput: { maxLength: 200 } }}
                    error={showRequiredErrors && !role.organization.trim()}
                    fullWidth
                  />
                  <Button
                    type="button"
                    onClick={() =>
                      update(
                        "pastRoles",
                        profile.pastRoles.filter((_, i) => i !== index),
                      )
                    }
                  >
                    {t("onboarding.profile.removePastRole")} {index + 1}
                  </Button>
                </Stack>
              </Box>
            ))}
            {profile.pastRoles.length < MAX_PAST_ROLES && (
              <Button
                type="button"
                onClick={() =>
                  update("pastRoles", [
                    ...profile.pastRoles,
                    { title: "", organization: "" },
                  ])
                }
              >
                {t("onboarding.profile.addPastRole")}
              </Button>
            )}
          </Stack>
        </Box>
        <TextField
          label={t("onboarding.profile.biography")}
          value={profile.biography}
          onChange={(event) =>
            update("biography", event.target.value.slice(0, 300))
          }
          multiline
          minRows={3}
          helperText={`${profile.biography.length}/300`}
          fullWidth
        />
        <Autocomplete
          multiple
          options={languagesSpoken}
          value={profile.languages}
          onChange={(_, value) => update("languages", value)}
          renderInput={(params) => (
            <TextField {...params} label={t("onboarding.profile.languages")} />
          )}
        />
        <FormSubmitButton
          data-profile-submit="true"
          variant="contained"
          fullWidth
          disabledReason={
            uploadsPending
              ? t("onboarding.profile.uploadPending")
              : !profileComplete
                ? t("onboarding.profile.incomplete")
                : currentRoleInvalid || pastRolesInvalid
                  ? t("onboarding.profile.employmentIncomplete")
                  : ""
          }
          sx={{ py: 1.5 }}
        >
          {t("onboarding.profile.continue")}
        </FormSubmitButton>
      </Stack>
    </Box>
  );
};

export default ProfileStep;
